import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from 'lucide-react';

import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import type { SrfImportValidationError } from '@/entities/consignments';

import { SRF_ERROR_FIELDS, srfErrorMessage } from '../lib/srf-import-errors';

interface SrfImportErrorsAlertProps {
  errors?: SrfImportValidationError[];
  onDismiss: () => void;
}

/**
 * Header banner for the `200` outcome of an SRF import: the file was read, but
 * fields were missing or unresolvable, so the backend created nothing. A warning
 * rather than a destructive alert — the import did not fail, it landed the user
 * here to finish the job. The wording comes from legacy RITA's own keys; only the
 * layout is ours. Each entry is also set on its form control, so this list is the
 * overview and the controls carry the same message in place.
 */
export function SrfImportErrorsAlert({ errors, onDismiss }: SrfImportErrorsAlertProps) {
  const { t } = useTranslation();

  if (!errors?.length) return null;

  return (
    <Alert variant="warning" className="relative">
      <AlertTriangle />
      <AlertTitle>{t('srf.validation.title', 'SRF imported — the consignment was not created')}</AlertTitle>
      <AlertDescription>
        <span>
          {t(
            'srf.validation.description',
            'These fields could not be filled in from the SRF file. Complete them and save to create the consignment.'
          )}
        </span>
        <ul className="mt-1 list-disc space-y-0.5 pl-4 text-xs">
          {errors.map((error, index) => {
            const labelKey = SRF_ERROR_FIELDS[error.field]?.labelKey;
            return (
              <li key={`${error.field}-${index}`}>
                {/* A key the form does not know falls back to the server's own message, unlabelled. */}
                {labelKey && <span className="font-medium">{t(labelKey)} — </span>}
                {srfErrorMessage(error, t)}
              </li>
            );
          })}
        </ul>
      </AlertDescription>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="absolute right-2 top-2 h-6 w-6"
        onClick={onDismiss}
        aria-label={t('gen.hide.label')}>
        <X className="h-4 w-4" />
      </Button>
    </Alert>
  );
}
